import { useMemo } from 'react';
import { isAfter, parseISO } from 'date-fns';
import type { TrialLessonDTO } from '@/lib/trials';

export type TrialWorkflowState = 'probestunde' | 'entscheidung' | 'anmeldung' | 'angebot' | 'unterricht';

export type TrialWorkflowActionId =
  | 'mark_occurred'
  | 'resend_confirmation'
  | 'reschedule'
  | 'cancel'
  | 'send_enrollment'
  | 'mark_not_started'
  | 'send_reminder'
  | 'edit_enrollment'
  | 'send_offer'
  | 'confirm_teaching'
  | 'start_course'
  | 'cancel_teaching'
  | 'consider_until'
  | 'mark_no_occurrence';

export type TrialWorkflowAction = {
  id: TrialWorkflowActionId;
  label: string;
  variant: 'primary' | 'secondary' | 'destructive';
};

export type TrialWorkflowResult = {
  state: TrialWorkflowState;
  stepIndex: number;
  actions: TrialWorkflowAction[];
  moreActions: TrialWorkflowAction[];
  anmeldungStatus: 'offen' | 'versendet' | 'abgeschlossen';
  angebotStatus: 'offen' | 'versendet' | 'abgeschlossen' | 'abgelehnt';
  /** Der Unterricht läuft — der Trichter ist ganz durchlaufen. */
  courseStarted: boolean;
};

const STEPS: TrialWorkflowState[] = ['probestunde', 'entscheidung', 'anmeldung', 'angebot', 'unterricht'];

function safeParseDateTime(dateStr?: string, timeStr?: string): Date | null {
  const d = (dateStr || '').trim();
  if (!d) return null;
  const t = (timeStr || '').trim();
  const hhmm = t.length >= 5 ? t.slice(0, 5) : '00:00';
  try {
    return parseISO(`${d}T${hhmm}:00`);
  } catch {
    return null;
  }
}

function isPastTrial(trial: TrialLessonDTO, now: Date): boolean {
  const endAt = safeParseDateTime(trial.date, trial.endTime || trial.startTime);
  if (!endAt) return false;
  return !isAfter(endAt, now);
}

function isOrderAccepted(status?: string): boolean {
  const value = String(status || '').toLowerCase();
  return value === 'angenommen' || value === 'bestätigt' || value === 'beendet';
}

function isOrderConfirmed(status?: string): boolean {
  const value = String(status || '').toLowerCase();
  return value === 'bestätigt' || value === 'beendet';
}

function isOrderDeclined(status?: string): boolean {
  return String(status || '').toLowerCase() === 'abgelehnt';
}

export function deriveTrialLessonWorkflow(trial: TrialLessonDTO, now: Date = new Date()): TrialWorkflowResult {
  const cancelled = trial.status === 'cancelled';
  const manuallyOccurred = trial.status === 'completed';
  const past = isPastTrial(trial, now) || manuallyOccurred;
  const enrollmentStatus = String(trial.enrollment?.status || '').toLowerCase();
  const orderStatus = String(trial.order?.status || '').toLowerCase();

  const enrollmentSent = enrollmentStatus === 'sent';
  const hasOpenEnrollment = enrollmentSent || enrollmentStatus === 'draft';
  const enrollmentCompleted = enrollmentStatus === 'accepted_by_student' || enrollmentStatus === 'converted';
  const orderAccepted = isOrderAccepted(orderStatus) || trial.status === 'started';
  const courseStarted = trial.status === 'started' || enrollmentStatus === 'converted';
  const orderDeclined = isOrderDeclined(orderStatus);

  // Sobald Anmeldung/Angebot fortgeschritten sind, darf der Flow nicht
  // auf "Probestunde" zurückfallen, nur weil der Termin noch in der Zukunft liegt.
  const state: TrialWorkflowState =
    orderAccepted
      ? 'unterricht'
      : (enrollmentCompleted || orderDeclined)
        ? 'angebot'
        : hasOpenEnrollment
          ? 'anmeldung'
          : (!past && !cancelled ? 'probestunde' : 'entscheidung');

  const anmeldungStatus: 'offen' | 'versendet' | 'abgeschlossen' = enrollmentCompleted
    ? 'abgeschlossen'
    : enrollmentSent
      ? 'versendet'
      : 'offen';
  const offerSent = trial.status === 'offer_sent';
  const angebotStatus: 'offen' | 'versendet' | 'abgeschlossen' | 'abgelehnt' = orderAccepted
    ? 'abgeschlossen'
    : orderDeclined
      ? 'abgelehnt'
      : offerSent
        ? 'versendet'
        : 'offen';

  const base: TrialWorkflowResult = {
    state,
    stepIndex: STEPS.indexOf(state),
    actions: [],
    moreActions: [],
    anmeldungStatus,
    angebotStatus,
    courseStarted,
  };

  if (cancelled) {
    return base;
  }

  if (state === 'probestunde') {
    return {
      ...base,
      actions: [
        { id: 'mark_occurred', label: 'Hat stattgefunden', variant: 'primary' },
        { id: 'resend_confirmation', label: 'Bestätigung erneut versenden', variant: 'secondary' },
        { id: 'reschedule', label: 'Verschieben', variant: 'secondary' },
        { id: 'cancel', label: 'Absagen', variant: 'secondary' },
      ],
    };
  }

  if (state === 'entscheidung') {
    return {
      ...base,
      actions: [
        { id: 'send_enrollment', label: 'Anmeldung zusenden', variant: 'primary' },
        // Gebaut, aber auf der Probestunden-Seite nicht erreichbar (T-0066).
        { id: 'consider_until', label: 'Überlegt noch', variant: 'secondary' },
        { id: 'mark_not_started', label: 'Fängt nicht an', variant: 'secondary' },
      ],
      moreActions: [
        { id: 'mark_no_occurrence', label: 'Termin hat nicht stattgefunden', variant: 'secondary' },
        // Nur solange die Probestunde noch ansteht — eine durchgeführte lässt der
        // Server weder verschieben noch absagen (T-0167). Nach Status, nicht nach
        // Phase: auch vergangene, nicht abgehakte landen in „Entscheidung“.
        ...(['scheduled', 'confirmed', 'rescheduled'].includes(String(trial.status))
          ? [
              { id: 'reschedule' as const, label: 'Probestunde verschieben', variant: 'secondary' as const },
              { id: 'cancel' as const, label: 'Probestunde absagen', variant: 'secondary' as const },
            ]
          : []),
      ],
    };
  }

  if (state === 'anmeldung') {
    return {
      ...base,
      actions: [
        { id: 'send_reminder', label: 'Erinnerung senden', variant: 'secondary' },
        { id: 'edit_enrollment', label: 'Anmeldung anpassen', variant: 'secondary' },
        { id: 'mark_not_started', label: 'Fängt nicht an', variant: 'secondary' },
      ],
    };
  }

  if (state === 'angebot') {
    if (angebotStatus === 'abgelehnt') {
      return {
        ...base,
        actions: [
          { id: 'send_offer', label: 'Angebot erneut senden', variant: 'primary' },
          { id: 'edit_enrollment', label: 'Anmeldung anpassen', variant: 'secondary' },
          { id: 'mark_not_started', label: 'Fängt nicht an', variant: 'secondary' },
        ],
      };
    }
    return {
      ...base,
      actions: [
        { id: 'send_offer', label: 'Angebot senden', variant: 'primary' },
        { id: 'edit_enrollment', label: 'Anmeldung anpassen', variant: 'secondary' },
        { id: 'mark_not_started', label: 'Fängt nicht an', variant: 'secondary' },
      ],
    };
  }

  // Nach der Zusage des Lehrers bestätigt das Büro den Auftrag — und startet damit
  // den Unterricht (Kursteilnahme, Kontingent). Ein eigener Schritt „Unterricht
  // starten“ daneben verwirrte (T-0293, T-0318); er bleibt nur für Aufträge, die
  // vorher schon bestätigt, aber nie gestartet wurden.
  const orderConfirmed = isOrderConfirmed(orderStatus);
  const teachingActions: TrialWorkflowAction[] = [];
  if (!courseStarted && !orderConfirmed) {
    teachingActions.push({ id: 'confirm_teaching', label: 'Unterricht bestätigen', variant: 'primary' });
  }
  if (!courseStarted && orderConfirmed) {
    teachingActions.push({ id: 'start_course', label: 'Unterricht starten', variant: 'primary' });
  }
  return {
    ...base,
    actions: [
      ...teachingActions,
      // Läuft der Unterricht, lässt sich die Anmeldung nicht mehr anpassen (T-0169).
      ...(courseStarted
        ? []
        : [{ id: 'edit_enrollment' as const, label: 'Anmeldung anpassen', variant: 'secondary' as const }]),
      // Vor dem ersten Unterricht: der Interessent fängt doch nicht an. Danach ist
      // es eine Kündigung — die läuft über Stundenplan oder Person (T-0072).
      ...(courseStarted
        ? []
        : [{ id: 'cancel_teaching' as const, label: 'Unterricht absagen', variant: 'secondary' as const }]),
    ],
  };
}

export function useTrialLessonWorkflow(trial: TrialLessonDTO, now?: Date): TrialWorkflowResult {
  return useMemo(() => deriveTrialLessonWorkflow(trial, now), [trial, now]);
}
